import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { merge, zip } from 'rxjs';
import { Settings } from '../../../account/models';
import { SettingService } from '../../../account/services';
import { User } from '../../../auth/models';
import { CurrencyHelper } from "../../../common/helpers";
import { DateTimeHelper } from "../../../common/helpers/datetime/datetime.helper";
import { UnitHelper } from "../../../common/helpers/unit/unit.helper";
import { CoverLetter, Currency, Document, Units } from "../../../common/models";
import { FieldConfig } from '../../../common/models/field-registry.model';
import { CoverLetterService, CurrencyRatesService, CurrencyService, FieldRegistryService, TenderDocumentsService, UnitRatesService } from "../../../common/services";
import { PlattsPricesService } from '../../../indexes/services';
import { indexRateKey } from '../../../indexes/services/platts/platts-prices.service';
import { BidRateTables, formulaWeightToVolume, loadBidRateTables, tableUsgPerMt } from '../../../common/helpers/unit/bid-unit-rates';
import { ToastService } from '../../../main/services';
import { BidHistoryStruct } from '../../../supplier/models';
import { AwardCompleteModalComponent } from '../../components';
import {
  Contract,
  ContractSupplier,
  ConversionFactor,
  ROUND_SUPPLIER_STATUSES,
  Round,
  Tender
} from '../../models';
import { CONTRACT_SUPPLIER_STATUSES } from '../../models/contract-supplier/contract-supplier.statuses';
import { Offer } from '../../models/offer';
import { ROUND_STATUSES } from '../../models/round';
import { AWAITING_OFFER_STATUSES } from '../../models/round-supplier/round-supplier.statuses';
import {
  TenderContractBidsAnalysisService,
  TenderContractSuppliersService,
  TenderContractsService,
  TenderService, TenderSupplierDocumentsService
} from '../../services';
import { TenderBidHistoryService } from '../../services/tender-bid-history/tender-bid-history.service';
import { TenderBidSubmitService } from "../../services/tender-bid-submit/tender-bid-submit.service";
import { FormulaParserService, FormulaConfig } from "../../../common/services/formula-parser.service";
import { AuthService } from "../../../auth/services";
import { BID_DETAIL_FIELD_KEYS } from './tender-contract-dashboard-supplier.fields';

@Component({
    selector: 'jft-tender-contract-dashboard-supplier',
    templateUrl: './tender-contract-dashboard-supplier.component.html',
    styleUrls: ['./tender-contract-dashboard-supplier.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class TenderContractDashboardSupplierComponent implements OnInit {
    public defaultDateFormat: string = DateTimeHelper.defaultDateLuxFormat();
    public dateFormat = 'YYYY-MM-DD';
    public unitRates;
    // The table at the tender's agreed rate, as the page was resolved: what "units default" restores.
    private tenderUnitRates;
    // The bid's own tables (decisions #46, #47): its rate, else the tender's, else 331.
    public rateTables: BidRateTables | null = null;
    public currencyRates;
    public baseCurrency: Currency;
    public baseUnit: Units;
    public baseExchangeRateDate: any = null;
    public tenderSettings: ConversionFactor;
    public tender: Tender;
    public contract: Contract;
    public offerStatuses = ROUND_SUPPLIER_STATUSES;
    public awaitingOfferStatuses = AWAITING_OFFER_STATUSES;
    public suppliers: ContractSupplier[] = [];
    public supplier: ContractSupplier;
    public dateNow = new Date();
    public contractSupplierStatuses = CONTRACT_SUPPLIER_STATUSES;
    public companySettings: Settings;
    public loadingAward: boolean = false;
    public roundNum: number;
    public round: any;
    public roundStatuses = ROUND_STATUSES;
    public rounds: Round[] = [];
    public loadingDecline: boolean = false;
    public dynamicFields: FieldConfig[] = [];
    public fieldConfigs: { [key: string]: FieldConfig } = {};

    public coverLetter: CoverLetter;
    public documents: Document[] = [];
    public supplierRequestedDocuments: Document[] = [];

    public bidHistory: BidHistoryStruct;
    private activeFormulas: Record<string, FormulaConfig> = {};

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public user: User,
        private settingsService: SettingService,
        protected toast: ToastService,
        private service: TenderContractBidsAnalysisService,
        private bidHistoryService: TenderBidHistoryService,
        private supplierService: TenderContractSuppliersService,
        private tenderContractService: TenderContractsService,
        private tenderService: TenderService,
        private documentsService: TenderDocumentsService,
        private supplierDocumentsService: TenderSupplierDocumentsService,
        private coverLetterService: CoverLetterService,
        private plattsPricesService: PlattsPricesService,
        private injector: Injector,
        private ngbModal: NgbModal,
        private currencyRatesService: CurrencyRatesService,
        private unitRatesService: UnitRatesService,
        protected documentService: TenderDocumentsService,
        private serviceBidSubmit: TenderBidSubmitService,
        private fieldRegistryService: FieldRegistryService,
        public currencies: CurrencyService,
        private formulaParser: FormulaParserService,
    ) {
    }

    public ngOnInit() {
        let parentData = this.route.parent.snapshot.data,
            data = this.route.snapshot.data,
            parentParentData = this.route.parent.parent.parent.snapshot.data;

        this.supplier = data['supplier'];
        this.suppliers = parentData['suppliers'];
        this.supplier.supplier.users = this.supplier.supplier.users.filter(user => !user.archived);
        this.contract = parentData['contract'];
        this.tender = parentParentData['tender'];
        this.rounds = parentData['rounds'];
        this.currencies = parentData['currencies'];
        this.coverLetter = data['coverLetter'];
        this.documents = data['documents'];
        this.bidHistory = data['bidHistory'];
        this.roundNum = this.route.snapshot.params.rid;
        this.round = this.rounds.filter(item => item.round == this.roundNum)[0];
        this.tenderSettings = data['tenderSettings'];

        // Load active formulas from company settings
        const customFields = this.user?.company?.settings?.custom_fields;
        this.activeFormulas = FormulaParserService.getActiveFormulas(
            customFields?.formulas,
            customFields?.deleted_formulas,
        );

        this.unitRates = data['unitRates'];
        this.tenderUnitRates = data['unitRates'] ? JSON.parse(JSON.stringify(data['unitRates'])) : null;
        this.baseCurrency = this.tenderSettings.currency
        this.baseUnit = this.tenderSettings.units

        if (this.tenderSettings.exchangeRateDate && this.tenderSettings.isExchangeRateFixed){
            this.baseExchangeRateDate = this.tenderSettings.exchangeRateDate.format(this.dateFormat);
        }

        this.settingsService.fetch().subscribe((settings) => {
            this.companySettings = settings;
            this.loadingDynamicFields();
        });

        this.initSupplierDocuments();

        this.getCurrencyRates();
    }

    private initSupplierDocuments() {
        if (this.supplier.supplierRequestedDocument) {
            this.supplier.supplierRequestedDocument.forEach((item) => {
                this.supplierRequestedDocuments.push(Document.create(item.file));
            });
        }
    }

    private loadingDynamicFields() {
        this.fieldRegistryService.getFieldConfigForTender(this.tender.id, 'bid').subscribe((config) => {
            // Build map for fast lookup
            config.forEach(f => {
                this.fieldConfigs[f.key] = f;
            });

            this.dynamicFields = config.filter(f => !BID_DETAIL_FIELD_KEYS.includes(f.key) && !f.key.startsWith('location_'));
        });
    }

    public award(offer: ContractSupplier) {
        this.loadingAward = true;
        let sumAwardedPercent = this.countSumAwardedPercent();

        this.openAwardCompleteModal(offer, sumAwardedPercent);
    }

    private submitAward(offer: ContractSupplier) {
        this.supplierService.award(this.tender, this.contract, offer).subscribe(() => {
            this.refresh(`Location: ${this.contract.location.getLabel()} awarded`);
            this.round.status = this.roundStatuses.AWARDED;

        }, (e) => {
            offer.awardedPercent = null;
            this.toast.error(e);
        });
    }

    private countSumAwardedPercent() {
        let sum = 0;
        this.suppliers.forEach(offer => {
            if (offer.awardedPercent) {
                sum += parseInt(offer.awardedPercent, 10);
            }
        })
        return sum;
    }

    public openAwardCompleteModal(awardedOffer, sumAwardedPercent) {
        const activeModal = this.ngbModal.open(AwardCompleteModalComponent, {
            injector: Injector.create([
                {provide: Tender, useValue: this.tender},
                {provide: Contract, useValue: this.contract},
                {provide: Offer, useValue: awardedOffer},
            ], this.injector),
            size: 'lg',
            backdrop: 'static'
        })

        activeModal.componentInstance.sumAwardedPercent = sumAwardedPercent;

        activeModal.result.then((data) => {
            this.submitAward(awardedOffer);
        }, (error) => {
            awardedOffer.awardedPercent = null;
            this.loadingAward = false;
        });
    }

    public resolveFieldValue(field: FieldConfig): string {
        if (!this.supplier || !this.supplier.bid) return '-';

        // Use internal_key for model access
        let key = field.internal_key || field.key;

        // Specific model mapping overrides
        const mappingOverrides: { [key: string]: string } = {
            'location_award_quota': 'volume_tolerance',
            'bid_fuel_availability_percentage': 'fuel_availability_percentage',
            'bid_delivery_point': 'fuelPreference',
            'bid_gross_or_net_billing': 'grossNetBilling',
            'bid_saf_mandate_blend': 'safMandateBlend'
        };
        if (mappingOverrides[key]) key = mappingOverrides[key];

        let value = this.supplier.bid[key];

        // Fallback: strip 'bid_' or 'location_' prefix
        if (value === undefined || value === null) {
            const strippedKey = key.replace(/^(bid_|location_)/, '');
            value = this.supplier.bid[strippedKey];
        }

        // Fallback to camelCase
        if (value === undefined || value === null) {
            const camelKey = key.replace(/_([a-z])/g, (g: string) => g[1].toUpperCase());
            value = this.supplier.bid[camelKey];

            // Try camelCase on stripped key too
            if (value === undefined || value === null) {
                const strippedKey = key.replace(/^(bid_|location_)/, '');
                const camelStrippedKey = strippedKey.replace(/_([a-z])/g, (g: string) => g[1].toUpperCase());
                value = this.supplier.bid[camelStrippedKey];
            }
        }

        // Special handling for nested structures (SAF, Market Price, Payment)
        // Note: Use field.key (original key) for conditions, but field.internal_key for property access
        if (value === undefined || value === null) {
            const originalKey = field.key;
            if (originalKey === 'bid_saf_fee_amount' && this.supplier.bid.safFeeAmountUnits) {
                value = this.supplier.bid.safFeeAmountUnits?.price?.value;
            } else if (originalKey === 'bid_neat_saf_mandated_price' && this.supplier.bid.neatSaf) {
                value = this.supplier.bid.neatSaf?.price?.value;
            } else if (originalKey === 'bid_market_price_value' && this.supplier.bid.marketPrice) {
                value = this.supplier.bid.marketPrice?.price?.['price']?.value;
            } else if (originalKey.startsWith('bid_payment_') || originalKey === 'bid_method_of_payment' || originalKey.startsWith('bid_open_invoice_') || originalKey.startsWith('bid_prepayment_') || originalKey === 'bid_guarantees_deposits_required') {
                const payment = this.supplier.bid.payment;
                if (payment) {
                    if (originalKey === 'bid_payment_currency') value = payment.currency;
                    else if (originalKey === 'bid_payment_unit') value = payment.unit;
                    else if (originalKey === 'bid_method_of_payment') value = payment.method_of_payment;
                    else if (originalKey === 'bid_guarantees_deposits_required') value = payment.guarantees_deposit;
                    else if (originalKey === 'bid_payment_exchange_financial_source') value = payment.exchange_rate?.financial_source;
                    else if (originalKey === 'bid_payment_averaging_method') value = payment.exchange_rate?.averageing_method;
                    else if (originalKey === 'bid_payment_averaging_offset') value = payment.exchange_rate?.averaging_offset;
                    else if (originalKey.startsWith('bid_open_invoice_')) {
                        // Use internal_key for property access
                        const internalKey = field.internal_key || originalKey.replace('bid_open_invoice_', '');
                        value = payment.open_invoice?.[internalKey];
                    } else if (originalKey.startsWith('bid_prepayment_')) {
                        // Use internal_key for property access (e.g., 'number_days_prepaid' instead of 'number_of_days_prepaid')
                        const internalKey = field.internal_key || originalKey.replace('bid_prepayment_', '');
                        value = payment.prepayment?.[internalKey];
                    }
                }
            }
        }

        if (value === null || value === undefined) return '-';

        // Handle dropdowns using config options
        if (field.options && field.options.length) {
            const targetId = typeof value === 'object' ? value.id : value;
            const option = field.options.find(o => o.id === targetId || o.id === value);
            if (option) return option.name;
        }

        // Handle generic objects with common property names
        if (typeof value === 'object') {
            // Handle objects with getLabel method (e.g., GrossNetBilling, FuelPreference)
            if (typeof value.getLabel === 'function') {
                const label = value.getLabel();
                if (label) return label;
            }
            // Handle nested gross_billing structure (for GrossNetBilling)
            if (value.grossBilling && value.grossBilling.name) return value.grossBilling.name;
            if (value.name) return value.name;
            if (value.code) return value.code;
            if (value.description) return value.description;
            if (value.price && value.price.value !== undefined) return value.price.value;
        }

        // Handle booleans
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        // Handle SAF Mandate (Y/N)
        if (field.key === 'bid_saf_mandate') {
            return value === 'Y' ? 'Yes' : value === 'N' ? 'No' : value;
        }

        // Handle numeric values (formatting)
        if (typeof value === 'number') {
           return value.toLocaleString('en-US', { maximumFractionDigits: 6 });
        }

        return value.toString();
    }

    private refresh(message: string) {
        zip(
            this.service.fetch(this.tender.id, this.contract.id),
            this.tenderContractService.renew(this.tender, this.contract),
            this.tenderService.renew(this.tender)
        ).subscribe((data) => {
            this.supplier = data[0][0];
            this.contract = data[1];
            this.tender = data[2];
            this.loadingAward = false;
            this.toast.success(message);
        });
    }

    public declineOffer(offer: ContractSupplier) {
        this.loadingDecline = true;
        this.supplierService.decline(this.tender, this.contract, offer.id).subscribe(() => {
            this.refresh(`Location: ${this.contract.location.getLabel()} offer was declined`);
        }, (e) => {
            this.loadingDecline = false;
            this.toast.error(e);
        });

    }

    public roundChanged(round) {
        this.round = round;
        let url = `/tenders/${this.tender.id}/locations/${this.contract.id}/dashboard/rounds/`
            + `${round.round}/suppliers/${this.supplier.supplier.id}`;
        this.router.navigateByUrl(url);
    }

    public isCurrentRound() {
        return this.supplier.round && this.supplier.round.round == this.contract.currentRound.round;
    }

    public updateDocumentsEvent($emit) {
        // Handle document deletion
        if ($emit && $emit.id) {
            this.deleteDocument($emit);
        } else {
            // Just refresh the documents
            this.updateDocuments();
            this.updateCoverLetter();
            this.updateSupplierDocuments();
        }
    }

    private deleteDocument(document: Document) {
        // Check if it's a cover letter file
        if (this.coverLetter && this.coverLetter.file && this.coverLetter.file.id === document.id) {
            this.coverLetterService.removeDocument(this.tender, this.coverLetter, document).subscribe(() => {
                this.toast.success(`Cover letter file: ${document.name} removed`);
                this.updateCoverLetter();
            }, (e) => {
                this.toast.error(e);
            });
        } else {
            // It's a regular document
            this.documentsService.remove(this.tender, document).subscribe(() => {
                this.toast.success(`Document: ${document.name} removed`);
                this.updateDocuments();
            }, (e) => {
                this.toast.error(e);
            });
        }
    }

    public updateDocuments() {
        this.documentsService.fetch(this.tender.id).subscribe((document: Document[]) => {
            this.documents = document;
        })
    }

    public updateCoverLetter() {
        this.coverLetterService.fetch(this.tender.id).subscribe((document: CoverLetter) => {
            this.coverLetter = document;
        })
    }

    public updateSupplierDocuments() {
        // this.supplierDocumentsService.fetch(this.tender.id).subscribe((document: Document[]) => {
        //     this.supplierRequestedDocuments = document;
        // })
    }

    public addDocumentFile(event: any) {
        this.createDocument(event.target.files)
    }

    public createDocument(files: File[]) {
        let observables = [];

        for (let i = 0; i < files.length; i ++) {
            let file = files[i];
            observables.push(this.documentsService.create(this.tender, file));
            this.toast.success(`Document: ${file.name} has been uploaded`);
        }

        merge(observables).subscribe((results) => {
            results.forEach((document) => {
                this.documents.push(document);
            });
        }, (e) => {
            this.toast.error(e);
        });
    }

    public onDateRange($event) {
        if (!this.supplier.bid.plattsIndexPrice) {
            return null;
        }

        this.getAveragePriceBase($event);
    }

    private getAveragePriceBase(event) {
        let params = {
            'currencyId': this.tender.currencyId,
            'unitId': this.tender.unitsId,
            'startDate': event.dateStart,
            'endDate': event.dateEnd,
            'tenderId': this.tender.id,
            'wToVolume': indexRateKey(this.supplier.bid),
        };

        this.plattsPricesService.fetchPriceAverage(this.supplier.bid.plattsIndexPrice.index.id,
            this.supplier.bid.plattsIndexPrice.bates.id, event.code, params)
            .subscribe((data) => {
                this.supplier.bid.plattsIndexPrice.value = data;
            }, (e) => {
                this.toast.error(e);
            });
    }

    public getBidHistory() {
        this.bidHistoryService.fetch(this.tender.id, this.contract.id, this.supplier.bid.id).subscribe(data => {
            this.bidHistory = data;
        })
    }

    private updateBidsData() {
            if (this.supplier.bid) {
                let item = this.supplier;
                if (item.bid.volume_based_fees) {
                    item.bid['volume_fees_total'] = Number(this.calcTotalFees(item.bid));
                }

                if (item.bid.perUsageFees) {
                    item.bid['per_uplift_fees_total'] = Number(this.calcTotalPerFees(item.bid));
                }

                let bid = item.bid;
                let diffValue: any = 0;
                let marketValue: any = 0;

                if (bid.differential && bid.differential.price) {
                    diffValue = Number(this.toCurrencyToUnit(bid.differential.price.value, bid.differential.price.currency.code, bid.differential.units.code));
                    item.bid['exchanged_differential'] = diffValue;
                }

                if (bid.marketPrice && bid.marketPrice.price) {
                    marketValue = Number(this.toCurrencyToUnit(bid.marketPrice.price['price']['value'], bid.marketPrice.price['price'].currency.code, bid.marketPrice.price['units'].code));
                    item.bid['exchanged_market_value'] = marketValue.toFixed(4)
                }

                // Build formula context and evaluate all active formulas
                const neatSafValue = 0; // SAF not readily computed on this page
                const costOfCapital = this.tenderSettings?.costOfCapital || 0;
                const paymentTerms = bid?.payment?.open_invoice?.payment_terms || 0;
                const preWaccTotal = parseFloat(diffValue) + (bid['volume_fees_total'] || 0) + parseFloat(marketValue) + neatSafValue;
                let waccComputed = 0;
                if (costOfCapital && paymentTerms) {
                    waccComputed = ((costOfCapital / 100) / 365) * paymentTerms * preWaccTotal * -1;
                }

                const formulaContext: Record<string, number> = {
                    differential: parseFloat(diffValue) || 0,
                    revised_differential: 0,
                    volume_fees_total: bid['volume_fees_total'] || 0,
                    saf_fee_amount: neatSafValue,
                    per_uplift_fees_total: bid['per_uplift_fees_total'] || 0,
                    index_value: 0,
                    market_price_value: parseFloat(marketValue) || 0,
                    weight_to_volume: formulaWeightToVolume(bid, tableUsgPerMt(this.unitRates), this.rateTables?.limits),
                    wacc: parseFloat(waccComputed.toFixed(4)),
                    payment_terms: paymentTerms,
                    cost_of_capital: costOfCapital,
                    average_uplift: 0,
                    contract_volume: Number(this.contract?.volume) || 0,
                    annual_frequency: Number(this.contract?.annualFrequency) || 0,
                    months_of_service: Number(this.contract?.monthService) || 0,
                    monthly_volume: this.contract?.monthService ? (Number(this.contract?.volume) || 0) / Number(this.contract.monthService) : 0,
                    available_volume: Number(bid.fuelAvailabilityPercentage) || 0,
                    saf_mandated_price: 0,
                    saf_voluntary_price: 0,
                    saf_blend_percentage: Number(bid.safMandateBlend) || 0,
                    volume_fees_all: 0,
                    per_uplift_fees_all: 0,
                    is_market_price: bid.marketPrice ? 1 : 0,
                    volume_per_uplift: 0,
                };

                for (const [formulaKey, formulaConfig] of Object.entries(this.activeFormulas)) {
                    try {
                        bid[formulaKey] = parseFloat(this.formulaParser.evaluate(formulaConfig.ast, formulaContext).toFixed(4));
                    } catch (e) {
                        bid[formulaKey] = 0;
                    }
                }

                // Backward-compatible aliases
                bid['diff_plus_vbfees'] = bid['diff_vol_fees'] || 0;
                bid['diff_plus_vbfees_saf'] = bid['diff_saf_vol_fees'] || 0;
                bid['diff_plus_vbfees_saf_hock'] = bid['diff_saf_vol_event_fees'] || 0;
                bid['diff_plus_vbfees_hock'] = bid['diff_vol_event_fees'] || 0;
            }
    }

    public calcTotalFees(bid) {
        let val = 0;
        bid['exchanged_volume_based_fees'] = [];
        bid.volume_based_fees.forEach((fee, index) => {
            if (fee.price && fee.price.price) {
                let currency = fee.price.price.currency.code;
                let unit = fee.price.units.code;
                let value = fee.price.price.value;
                let originalValue = value;
                value = this.convertToCurrency(currency, value);
                value = this.convertToUnit(unit, value);

                val += value;
                bid['exchanged_volume_based_fees'].push({
                    'name': fee.name,
                    'value': value.toFixed(4),
                    'original': {
                        'value': originalValue,
                        'currency': currency,
                        'unit': unit
                    }
                })
            }
        })
        // @ts-ignore
        return Number(val.toFixed(4));
    }

    public calcTotalPerFees(bid) {
        let val = 0;
        bid['exchanged_per_fees'] = [];
        bid.perUsageFees.forEach((fee, index) => { // perUsageFees
            if (fee.price) {
                let currency = fee.price.currency.code;
                let value = fee.price.value;
                let originalValue = value;
                value = this.convertToCurrency(currency, value);

                // Ensure value is a valid number before adding
                if (!isNaN(value) && isFinite(value)) {
                    val += value;
                }
                bid['exchanged_per_fees'].push({
                    'name': fee.name,
                    'value': (!isNaN(value) && isFinite(value) ? value : 0).toFixed(4),
                    'original': {
                        'value': originalValue,
                        'currency': currency,
                    }
                })
            }
        })
        // @ts-ignore
        // Ensure we never return NaN
        return (isNaN(val) || !isFinite(val) ? 0 : val).toFixed(4);
    }

    /** The Jet A-1 table of the bid shown: its own rate's, else the tender's. */
    public bidJetTable() {
        return this.rateTables ? this.rateTables.ofBid(this.supplier?.bid).jet : this.unitRates;
    }

    private convertToUnit(unitCode: string, value: number) {
        return UnitHelper.convertToUnit(unitCode, value, this.bidJetTable(), this.baseUnit.code);
    }

    private convertToCurrency(currency: string, value: number) {
        return CurrencyHelper.convertToCurrency(currency, value, this.currencyRates, this.baseCurrency.code);
    }

    /** Whether the supplier's input differs from the base currency/unit it is displayed in. */
    public isConverted(currencyCode: string, unitCode: string = null) {
        return CurrencyHelper.isConverted(
            currencyCode, this.baseCurrency?.code, unitCode, this.baseUnit?.code
        );
    }

    public toCurrencyToUnit(value: number, currency: string, unit: string) {
        let val = this.convertToCurrency(currency, value);
        val = this.convertToUnit(unit, val);
        return val.toFixed(6);
    }

    public getCurrencyRates() {
        this.currencyRatesService.fetch(this.baseCurrency.code, {tender: this.tender.id, date: this.baseExchangeRateDate}, false).subscribe((currencies: []) => {
            this.currencyRates = currencies;
            loadBidRateTables(this.unitRatesService, this.unitRates, [this.supplier?.bid]).subscribe(tables => {
                this.rateTables = tables;
                this.updateBidsData();
            });
        })
    }

    /** "Units default": back to the table at the tender's agreed rate (as resolved for the page),
     * never the plain 331 one, and the bid shown keeps converting at its own rate. */
    public getUnitRates($event?) {
        this.unitRates = JSON.parse(JSON.stringify(this.tenderUnitRates ?? this.unitRates ?? {}));
        loadBidRateTables(this.unitRatesService, this.unitRates, [this.supplier?.bid]).subscribe(tables => {
            this.rateTables = tables;
            this.updateBidsData();
        });
    }

    public downloadDocument(doc: Document) {
        return this.documentService.fetchFile(this.tender.id, doc);
    }

    public onUpdatedBidModal(event, offer: Offer) {
        // Handle new callback structure from bid edit modal
        const offcanvas = event.offcanvas || event;
        const onComplete = event.onComplete;

        this.refresh(`Location: ${this.contract.location.getLabel()} offer has been updated`);
        offcanvas.close('Close modal');
        if (onComplete) onComplete();
    }

    public getAllDocuments(): Document[] {
        const allDocs = [];

        // Add cover letter if exists
        if (this.coverLetter && this.coverLetter.file) {
            allDocs.push(this.coverLetter.file);
        }

        // Add buyer documents
        if (this.documents && this.documents.length) {
            allDocs.push(...this.documents);
        }

        // Add supplier documents
        if (this.supplierRequestedDocuments && this.supplierRequestedDocuments.length) {
            allDocs.push(...this.supplierRequestedDocuments);
        }

        return allDocs;
    }
}
